import { notifyNewChannelMessageApiCall, notifyNewChatMessageApiCall } from '@guryou/api-client-gql';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  startAfter,
  updateDoc,
  where,
} from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';

import { db, storage } from './firebase';

export const generateChatId = (users, providerId) => {
  const sortedUsers = users.sort();
  return `${sortedUsers.join('_')}_${providerId}`;
};

/**
 * The chat between these users, without creating it: the existing one, or a
 * draft ({ draft: true }) that createChat writes when its first message is sent,
 * so picking someone and leaving never leaves an empty chat behind.
 */
export const findChat = async (usersId, providerId) => {
  const chatId = generateChatId([...usersId], providerId);
  const chatSnap = await getDoc(doc(db, 'chats', chatId));
  if (chatSnap.exists()) return { id: chatSnap.id, ...chatSnap.data() };
  return { id: chatId, chatId, members: usersId.map(String), providerId, lastMessage: null, draft: true };
};

/** Writes a draft chat (findChat) — before its first message, whose rules read its members. */
export const createChat = async chat => {
  await setDoc(doc(db, 'chats', chat.id), {
    chatId: chat.id,
    members: chat.members.map(String),
    createdAt: serverTimestamp(),
    lastMessage: null,
    providerId: chat.providerId,
  });
};

export const fetchChannelsWhereUserIsMember = async channelId => {
  const docRef = doc(db, 'channels', channelId);
  const chatSnap = await getDoc(docRef);

  return { id: chatSnap.id, ...chatSnap.data() };
};

// `pageSize`: only the newest messages are live (older ones load with
// loadOlderMessages as the conversation scrolls up); without it, all of them.
export const listenToChatMessages = async (type, chatId, callback, pageSize) => {
  const collectionRef = type === 'channel' ? `channels/${chatId}/messages` : `chats/${chatId}/messages`;

  try {
    const messagesRef = collection(db, collectionRef);
    const messagesQuery = pageSize
      ? query(messagesRef, orderBy('createdAt', 'desc'), limit(pageSize))
      : query(messagesRef, orderBy('createdAt', 'asc'));

    const unsubscribe = onSnapshot(
      messagesQuery,
      snapshot => {
        const messages = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        callback(pageSize ? messages.reverse() : messages);
      },
      error => {
        // Previously just `return error` here — the error callback's return value goes
        // nowhere, so callback() was never called again and every caller (both
        // AthleteChatPanel.js and providers/chat/index.js) tracks "loading" as
        // "callback hasn't fired yet", meaning any snapshot error (permission-denied,
        // bad path, offline) left the UI spinning forever with no signal at all.
        // eslint-disable-next-line no-console
        console.error('listenToChatMessages snapshot error:', error);
        callback([]);
      }
    );

    return unsubscribe;
  } catch (error) {
    // Same bug as above, one level up: `return error` resolves the async function
    // normally, so callers doing `await listenToChatMessages(...)` get an Error object
    // back instead of an unsubscribe function (harmless, since callers guard with
    // `typeof maybeUnsub === 'function'`) — but callback() is never invoked, so the UI
    // never leaves its loading state.
    // eslint-disable-next-line no-console
    console.error('listenToChatMessages error:', error);
    callback([]);
    return null;
  }
};

/** The `pageSize` messages before `beforeCreatedAt`, oldest first (one page further up the conversation). */
export const loadOlderMessages = async (type, chatId, beforeCreatedAt, pageSize) => {
  const messagesRef = collection(db, type === 'channel' ? `channels/${chatId}/messages` : `chats/${chatId}/messages`);
  const snapshot = await getDocs(query(messagesRef, orderBy('createdAt', 'desc'), startAfter(beforeCreatedAt), limit(pageSize)));
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).reverse();
};

// EARS 4 (.kiro/specs/1596-chat-notifications-rework): `lastMessage` on the
// conversation doc is the only write a send needs — every reader derives
// their own unread state from it (isConversationUnread) against their own
// readReceipts entry. No separate per-recipient array to keep in sync.
export const sendMessageInChannel = async (chatId, message) => {
  const messagesRef = collection(db, `channels/${chatId}/messages`);
  const newMessage = await addDoc(messagesRef, message);
  await updateDoc(doc(db, `channels/${chatId}`), {
    lastMessage: { ...message, id: newMessage.id },
  });
  // Push notification (.kiro/specs/1598-chat-push-notifications-web) — sent
  // by the API (ChatPushAPI.notifyNewChannelMessage), which re-reads this
  // same lastMessage from Firestore as the source of truth rather than
  // trusting client-supplied content. Best-effort: never blocks/fails the
  // send itself if the push call errors.
  notifyNewChannelMessageApiCall({ channelId: chatId }).catch(() => null);
};

export const sendMessageInChat = async (chatId, message) => {
  const messagesRef = collection(db, `chats/${chatId}/messages`);
  const newMessage = await addDoc(messagesRef, message);
  await updateDoc(doc(db, `chats/${chatId}`), { lastMessage: { ...message, id: newMessage.id } });
  // Push notification — see sendMessageInChannel's comment above; same
  // ChatPushAPI-backed, best-effort, source-of-truth-from-Firestore model.
  notifyNewChatMessageApiCall({ chatId }).catch(() => null);
};

// Single atomic field write on a specific map key — Firestore merges just
// that key, so there is no read-then-write step and no array-membership
// operation. This is what makes EARS 1/2 true by construction: the race in
// the old removeNotification (getDoc → filter → updateDoc the whole array)
// and the arrayRemove type-mismatch in the old removeNotificationChannel
// (arrayRemove requires exact value+type match against how the array was
// populated) can't happen here — there's no array at all anymore.
export const markChatRead = (chatId, userId) => updateDoc(doc(db, 'chats', chatId), { [`readReceipts.${userId}`]: Date.now() });

export const markChannelRead = (channelId, userId) => updateDoc(doc(db, 'channels', channelId), { [`readReceipts.${userId}`]: Date.now() });

export const deleteMessage = (chatId, messageId) => {
  const messagesRef = doc(db, `chats/${chatId}/messages/${messageId}`);
  return deleteDoc(messagesRef);
};

export const deleteMessageChannel = (chatId, messageId) => {
  const messagesRef = doc(db, `channels/${chatId}/messages/${messageId}`);
  return deleteDoc(messagesRef);
};

export const uploadChat = async (file, onProgress) => {
  const imageRef = ref(storage, `chat_images/${Date.now()}_${file.name}`);

  const uploadTask = uploadBytesResumable(imageRef, file);

  return new Promise((resolve, reject) => {
    uploadTask.on(
      'state_changed',
      snapshot => {
        // Progress function
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        if (onProgress) {
          onProgress(Math.round(progress));
        }
      },
      error => {
        // Error function
        reject(error);
      },
      async () => {
        // Complete function
        try {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          resolve(url);
        } catch (error) {
          reject(error);
        }
      }
    );
  });
};

export const getFileNameFromUrl = url => {
  try {
    const decodedUrl = decodeURIComponent(url);
    const match = decodedUrl.match(/\/o\/(.+?)\?/);
    if (!match || match.length < 2) {
      return null;
    }

    const fullPath = match[1];
    const rawFileName = fullPath.split('/').pop();
    const nameWithoutPrefix = rawFileName.substring(rawFileName.indexOf('_') + 1);

    const parts = nameWithoutPrefix.split('.');
    if (parts.length > 2) {
      parts.pop();
      return parts.join('.');
    }

    return nameWithoutPrefix;
  } catch (error) {
    return null;
  }
};

export const downloadFromFirebase = async docUrl => {
  try {
    const response = await fetch(docUrl);
    const blob = await response.blob();

    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `${getFileNameFromUrl(docUrl)}`;
    document.body.appendChild(link);
    link.click();

    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    return error;
  }
};

export const listenToChats = (userId, providerId, callback, errorCallback) => {
  try {
    const chatsRef = collection(db, 'chats');
    // members is stored as strings (see createChat) — EARS 7
    const q = query(chatsRef, where('providerId', '==', +providerId), where('members', 'array-contains', String(userId)));

    const unsubscribe = onSnapshot(
      q,
      snapshot => {
        const allChats = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        callback(allChats);
      },
      error => {
        if (errorCallback) {
          errorCallback(error);
        }
      }
    );

    return unsubscribe;
  } catch (error) {
    if (errorCallback) {
      errorCallback(error);
    }
    throw error;
  }
};

// Provider-agnostic variant of listenToChats — every chat this user is a
// member of, across every provider. Used by the global badge/toast
// (useUnreadChats/useUnreadMessages), which aggregate across the user's
// whole account, not one provider's chat page.
export const listenToMyChats = (userId, callback, errorCallback) => {
  try {
    const chatsRef = collection(db, 'chats');
    // members is stored as strings (see createChat) — EARS 7
    const q = query(chatsRef, where('members', 'array-contains', String(userId)));

    const unsubscribe = onSnapshot(
      q,
      snapshot => callback(snapshot.docs.map(d => ({ id: d.id, ...d.data() }))),
      error => {
        if (errorCallback) {
          errorCallback(error);
        }
      }
    );

    return unsubscribe;
  } catch (error) {
    if (errorCallback) {
      errorCallback(error);
    }
    throw error;
  }
};

export const listenToChannels = (providerId, callback, errorCallback) => {
  try {
    const chatsRef = collection(db, 'channels');
    const q = query(chatsRef, where('providerId', '==', +providerId));

    const unsubscribe = onSnapshot(
      q,
      snapshot => {
        const allChats = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        callback(allChats);
      },
      error => {
        if (errorCallback) {
          errorCallback(error);
        }
      }
    );

    return unsubscribe;
  } catch (error) {
    if (errorCallback) {
      errorCallback(error);
    }
    throw error;
  }
};

// Provider-agnostic variant of listenToChannels — every channel across every
// provider in `providerIds` (every provider the calling user is associated
// with). Firestore's `in` operator caps at 10 values; chunk providerIds at
// the call site if a user could ever be associated with more than that.
export const listenToAllChannelsForUser = (providerIds, callback, errorCallback) => {
  if (!providerIds?.length) {
    callback([]);
    return () => {};
  }
  try {
    const channelsRef = collection(db, 'channels');
    const q = query(channelsRef, where('providerId', 'in', providerIds));

    const unsubscribe = onSnapshot(
      q,
      snapshot => callback(snapshot.docs.map(d => ({ id: d.id, ...d.data() }))),
      error => {
        if (errorCallback) {
          errorCallback(error);
        }
      }
    );

    return unsubscribe;
  } catch (error) {
    if (errorCallback) {
      errorCallback(error);
    }
    throw error;
  }
};

// `image`: the channel's photo URL (uploaded with uploadChat), shown on the web and in the app.
export const createNewChannel = async (name, creator, providerId, image = null) => {
  const newChannel = {
    name,
    providerId,
    lastMessage: null,
    creator,
    image,
  };
  const channel = await addDoc(collection(db, 'channels'), newChannel);
  return {
    ...newChannel,
    id: channel.id,
  };
};

// A channel's name and photo, changed after it was created.
export const updateChannel = async (channelId, { name, image }) => {
  await updateDoc(doc(db, 'channels', channelId), { name, image: image || null });
};

export const removeChannel = async channelId => {
  await deleteDoc(doc(db, 'channels', channelId));
};

export const removeChat = async chatId => {
  await deleteDoc(doc(db, 'chats', chatId));
};
